import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import React, { forwardRef, useImperativeHandle, useRef } from 'react'

import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'

import Chevron from '../Icon/Chevron'
import { SelectableButton } from '../SelectableButton'

import { carousel, chevronLeft, chevronRight } from './styles.css'

export type Option = {
  label: string
  value: string
}

type Props = {
  className?: string
  error?: string
  name: 'hourRange' | 'dayRange'
  onSelectedItem: (o: Option) => void
  options: Option[]
  touched: boolean
  value: string
}

export type CarouselRef = {
  current: HTMLDivElement | null
  resetScroll: () => void | undefined
}

export const ButtonCarousel = forwardRef<CarouselRef, Props>(
  ({ className, name, onSelectedItem, options, value }, ref) => {
    const carouselRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()

    useImperativeHandle(ref, () => ({
      current: carouselRef.current,
      resetScroll,
    }))

    const resetScroll = () => {
      if (carouselRef.current) {
        carouselRef.current.scrollTo({
          behavior: 'smooth',
          left: 0,
        })
      }
    }

    const onSelect = (v: string, i: number) => {
      onSelectedItem(options[i])
    }

    /** width of a button (80px) + gap (16px) */
    const scrollAmount = 96

    function scrollCarousel(direction: 'prev' | 'next') {
      const position = carouselRef.current.scrollLeft
      carouselRef.current.scrollTo({
        behavior: 'smooth',
        left: direction === 'prev' ? position - scrollAmount : position + scrollAmount,
      })
    }

    const checkIsSelected = (value: string, selectedValue: string) => {
      if (name === 'hourRange') return value.toString() === selectedValue.toString()
      return value.toString().substring(0, 10) === selectedValue.toString().substring(0, 10)
    }

    return (
      <React.Fragment>
        <div className={cn(sprinkles({ display: 'flex' }), className)} role="radiogroup">
          <button
            aria-label={t('common:prev')}
            className={sprinkles({ padding: 'm' })}
            type="button"
            onClick={() => scrollCarousel('prev')}
          >
            <Chevron className={chevronLeft} />
          </button>
          <div ref={carouselRef} className={carousel} data-testid={`carousel-${name}`}>
            {options.map((option, idx) => (
              <SelectableButton
                key={option.value}
                dataTestId={`carousel-${name}-${option.value}`}
                selected={checkIsSelected(option.value, value)}
                value={option.value}
                onSelect={(s: string) => onSelect(s, idx)}
              >
                {option.label}
              </SelectableButton>
            ))}
          </div>
          <button
            aria-label={t('common:next')}
            className={sprinkles({ padding: 'm' })}
            type="button"
            onClick={() => scrollCarousel('next')}
          >
            <Chevron className={chevronRight} />
          </button>
        </div>
      </React.Fragment>
    )
  }
)
